
import React, { useCallback, useRef, useState } from 'react';
import {
  DataGrid,
} from 'devextreme-react/data-grid';
import { t } from 'i18next';
import { Popup } from 'devextreme-react/popup';
import { Button, LoadPanel, Switch, TextArea } from 'devextreme-react';
import { accountRetailerService } from '@/projects/platform/services/account-retailer-service';
import DataGridWrapper from '@/core/wrappers/data-grid-wrapper';
import CustomStore from 'devextreme/data/custom_store';
import { SupplierLocationAuthsView } from '@/core/models/supplier-location-auths-view';
import { toast } from 'react-toastify';
import { ApprovalRequestTimeline } from '_metronic/partials/layout/approval-request/approval-request-history';
import { createCustomStore } from '@/core/wrappers/custom-store-wrapper';


const App = () => {
  const [currentRow, setCurrentRow] = useState({ supplierId: null, title: '' });
  const [loadPanelVisible, setLoadPanelVisible] = useState(false);
  const [popupVisible, setPopupVisible] = useState(false);
  const [supplierLocationAuthStore, setDataSource] = useState<SupplierLocationAuthsView[]>([]);
  const dataGridRef = useRef<DataGrid>(null);
  const popupGridRef = useRef<DataGrid>(null);
  const commentAreaRef = useRef<TextArea>(null);
  const [approvalHistoryPopupVisible, setApprovalHistoryPopupVisible] = useState(false);
  const [currentApprovalSteps, setCurrentApprovalSteps] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState<any | null>(null);

  const loadData = useCallback((supplierId: any, defaultStockShare?: any, defaultSalesShare?: any) => {
    accountRetailerService.listSupplierLocationAuths(supplierId).then((response) => {
      setDataSource(response.data.searchResult)
      // İlk authorization kaydından sonra eklenen yeni bir depo var ise default değeri varsayılan değerlerden okunacak.
      const newLocations = response.data.searchResult.filter((x: SupplierLocationAuthsView) => x.maxAuthCreateDate != null
        && (new Date(x.maxAuthCreateDate ?? 0) < new Date(x.locationCreateDate ?? 0)))
      newLocations.map((x: SupplierLocationAuthsView) => {
        x.shareTheSales = defaultSalesShare;
        x.shareTheStock = defaultStockShare;
      })

    })
      .catch((err) => {
        throw new Error('Data Loading Error');
      });
  }, [setDataSource]);

  const editSupplierLocationAuth = useCallback((e: any) => {
    if (e.row.data.supplierAccountId !== null && e.row.data.supplierAccountId !== undefined && e.row.data.supplierAccountId !== '') {
      setCurrentRow({ supplierId: e.row.data.supplierAccountId, title: e.row.data.name })
      loadData(e.row.data.supplierAccountId, e.row.data.defaultStockShare, e.row.data.defaultSalesShare);
    }
    setPopupVisible(true);

  }, [setPopupVisible, setCurrentRow, setDataSource]);

  const hideInfo = useCallback(() => {
    setCurrentRow({ supplierId: null, title: '' });
    setPopupVisible(false);
  }, [setCurrentRow, setPopupVisible]);

  const calculateCellValue = (data: any) => currentRow.supplierId;

  const onToolbarPreparing = (e: any) => {
    e.toolbarOptions.items.unshift({
      location: 'before',
      widget: 'dxButton',
      options: {
        text: t('select_all'),
        icon: 'selectall',
        onClick: () => {
          if (popupGridRef.current) {
            const items = popupGridRef.current.props.dataSource as any[] | null | undefined;
            if (items) {
              items.forEach(element => {
                element.shareTheStock = true;
                element.shareTheSales = true;
              });
            }
            popupGridRef.current.instance.refresh();
          }
        },
      },
    },
      {
        location: 'before',
        widget: 'dxButton',
        options: {
          text: t('unselect_all'),
          icon: 'unselectall',
          onClick: () => {
            if (popupGridRef.current) {
              const items = popupGridRef.current.props.dataSource as any[] | null | undefined;
              if (items) {
                items.forEach(element => {
                  element.shareTheStock = false;
                  element.shareTheSales = false;
                });
              }
              popupGridRef.current.instance.refresh();
            }
          },
        },
      },
      {
        location: 'after',
        widget: 'dxButton',
        options: {
          text: '',
          icon: 'save',
          onClick: async () => {
            if (popupGridRef.current) {
              const items = popupGridRef.current.props.dataSource as any[] | null | undefined;
              if (items) {
                // sadece insert ve update edilecek olanlar çekiliyor
                const upsertedParams = items.filter(x => (x.supplierId == '00000000-0000-0000-0000-000000000000' && (x.shareTheStock == false || x.shareTheSales == false))
                  || (x.supplierId != '00000000-0000-0000-0000-000000000000'))
                  .map(element => ({
                    supplierId: currentRow.supplierId,
                    locationId: element.locationId,
                    sharedTheStock: element.shareTheStock,
                    sharedTheSales: element.shareTheSales,
                    locationCode: element.locationCode
                  }));

                const params = { supplierLocationAuthorizations: upsertedParams };
                await accountRetailerService.upsertSupplierLocationAuths(params).then(res => {
                  toast.success(t("saved_changes"));
                  setPopupVisible(false);
                }).catch(err => {
                  if (err.status === 504) {
                    toast.success(t("saved_changes"));
                    setPopupVisible(false);
                  } else {
                    toast.error(t("error"));
                  }
                });
              }
            }
          },
        },
      }, {
      location: 'after',
      widget: 'dxButton',
      options: {
        text: '',
        icon: 'revert',
        onClick: () => {
          if (popupGridRef.current) {
            loadData(currentRow.supplierId);
            popupGridRef.current.instance.refresh();
          }
        },
      },
    },
    );
  };


  const setValueStock = (rowValue: any, newValue: any) => {
    rowValue.row.data.shareTheStock = newValue.value;
  }

  const setValueSales = (rowValue: any, newValue: any) => {
    rowValue.row.data.shareTheSales = newValue.value;
  }

  const [accountStore] = useState(() => createCustomStore({
    key: 'id',
    load: accountRetailerService.lookupSuppliers,
    byKey: (key, loadOptions) => {
      let returnObject = null;
      if (key) {
        loadOptions.filter = ["Id", "=", key];
      }
      return returnObject
    },
  }));

  const onApprovalHistoryPopupHiding = () => {
    setSelectedRequest(null);
    setApprovalHistoryPopupVisible(false);
  };

  const handleRowUpdating = async (e: any) => {
    e.newData = { ...e.oldData, ...e.newData }
  };


  const store = createCustomStore({
    key: ["supplierAccountId"],
    load: accountRetailerService.getSupplierRelationRequests,
    update: accountRetailerService.updateSupplierRelation,
    byKey: (key, loadOptions) => {
      if (key) {
        loadOptions.filter = ["SupplierAccountId", "=", key];
      }
    },
  });

  const approveRequest = async () => {
    try {
      setLoadPanelVisible(true);
      const comment = commentAreaRef.current?.instance.option("value");
      await accountRetailerService.approveSupplierRelationRequest({ id: selectedRequest.id, comment })
      setLoadPanelVisible(false);
      setApprovalHistoryPopupVisible(false);
      commentAreaRef.current?.instance.option("value", undefined);
      await dataGridRef.current?.instance.refresh();
    } catch (error: any) {
      console.log(error)
      setLoadPanelVisible(false);
      toast.error(error.response.data.message);
    }
  }

  const rejectRequest = async () => {
    try {
      setLoadPanelVisible(true);
      const comment = commentAreaRef.current?.instance.option("value");
      await accountRetailerService.rejectSupplierRelationRequest({ id: selectedRequest.id, comment: comment })
      setLoadPanelVisible(false);
      setApprovalHistoryPopupVisible(false);
      commentAreaRef.current?.instance.option("value", undefined);
      await dataGridRef.current?.instance.refresh();
    } catch (error: any) {
      console.log(error)
      setLoadPanelVisible(false);
      toast.error(error.response.data.message);
    }
  }



  return (
    <div className='view-wrapper'>
      <LoadPanel
        shadingColor="rgba(0,0,0,0.4)"
        visible={loadPanelVisible}
        showIndicator={true}
        shading={true}
        showPane={true}
        hideOnOutsideClick={false}
        message={t("saving_please_wait")}

      />
      <DataGridWrapper
        props={{ id: 'supplier-management', ref: dataGridRef }}
        permissonCode='Retailer.SupplierRelation'
        datasource={store}
        onRowUpdating={handleRowUpdating}
        filterVisible={true}
        headerFilterVisible={true}
        pagerVisible={true}
        remoteOperations={true}
        enableCustomToolbarContent={false}
        editingConfig={{
          mode: 'batch',
          allowAdding: false,
          allowUpdating: true,
          allowDeleting: false
        }}
        columns={[
          {
            dataField: 'supplierAccountId',
            caption: t('supplier'),
            allowEditing: false,
            lookup: {
              dataSource: accountStore,
              valueExpr: 'id',
              displayExpr: 'name',
            },
          },
          {
            dataField: 'code',
            caption: t('code'),
            allowFiltering: true,
            allowEditing: true,
          },
          {
            dataField: 'isLocationShared',
            caption: t('isLocationShared'),
            allowFiltering: false,
            allowEditing: true,
          },
          {
            dataField: 'defaultStockShare',
            caption: t('defaultStockShare'),
            allowFiltering: false,
            allowEditing: true,
            width: 270
          },
          {
            dataField: 'defaultSalesShare',
            caption: t('defaultSalesShare'),
            allowFiltering: false,
            allowEditing: true,
            width: 270
          },
          {
            dataField: 'status',
            caption: t('status'),
            allowFiltering: false,
            allowEditing: false,
            width: 150
          },
          {
            type: 'buttons',
            width: 110,
            buttons: [
              {
                hint: 'Authorize Location',
                icon: 'find',
                name: 'auth',
                onClick: editSupplierLocationAuth,
              },
              {
                icon: 'ordersbox',
                hint: t("request_history"),
                visible: ((e: any) => !e.row.isEditing && e.row.data.approvalSteps && e.row.data.approvalSteps.length > 0),
                onClick: (e: any) => {
                  setSelectedRequest(e.row.data);
                  setCurrentApprovalSteps(e.row.data.approvalSteps);
                  setApprovalHistoryPopupVisible(true);
                }
              }
            ]
          }
        ]}
      />
      <Popup
        visible={popupVisible}
        onHiding={hideInfo}
        showTitle={true}
        width={1000}
        height={600}
        position={{
          of: window,
          at: 'center',
          my: 'center'
        }}
        title={currentRow.title.toString()}
      >
        <DataGridWrapper
          datasource={supplierLocationAuthStore}
          pagerVisible={true}
          filterVisible={true}
          editingConfig={{
            allowUpdating: true,
            allowDeleting: false,
            mode: "row"
          }}
          headerFilterVisible={true}
          onToolbarPreparing={onToolbarPreparing}
          props={{
            id: "supplier-location-list",
            remoteOperations: false,
            ref: popupGridRef
          }}
          columns={[
            {
              caption: t('supplier_Id'),
              calculateCellValue: calculateCellValue,
              visible: false,
            },
            {
              dataField: 'locationName',
              caption: t('location_name'),
              allowEditing: false,
            },
            {
              dataField: 'locationCode',
              caption: t('location_code'),
              allowEditing: false,
            },
            {
              dataField: 'locationType',
              caption: t('location_type'),
              allowEditing: false,
            },
            {
              dataField: 'locationId',
              caption: t('location_id'),
              visible: false,
            },
            {
              caption: t('share_the_stock'),
              cellRender: (data: any) => {
                const handleValueChange = (e: any) => {
                  setValueStock(data, e);
                };
                return (
                  <Switch
                    defaultValue={data.key.shareTheStock}
                    disabled={false}
                    onValueChanged={handleValueChange}
                  />
                );
              },
            },
            {
              caption: t('share_the_sales'),
              cellRender: (data: any) => {
                const handleValueChange = (e: any) => {
                  setValueSales(data, e);
                };
                return (
                  <Switch
                    defaultValue={data.key.shareTheSales}
                    disabled={false}
                    onValueChanged={handleValueChange}
                  />
                );
              },
            },
          ]}
        />
      </Popup>
      <Popup
        visible={approvalHistoryPopupVisible}
        onHiding={onApprovalHistoryPopupHiding}
        showTitle={true}
        width={600}
        height={selectedRequest && selectedRequest.statusCode === "Awaiting Approval" ? 500 : 400}
        position={{
          of: window,
          at: 'center',
          my: 'center'
        }}
        title={t("approval_history")}
      >
        <ApprovalRequestTimeline timelines={currentApprovalSteps} />
        {selectedRequest && selectedRequest.statusCode === "Awaiting Approval" ? (<div>
          <TextArea
            ref={commentAreaRef}
            height={70}
            className='mt-10'
            readOnly={false}
            label={t("comment") + ':'}
          />
          <div className='row mt-4 '>
            <div className='col mt-2 mb-2 '>
              <div className='row justify-content-end'>
                <div className='col-auto'>
                  <Button
                    text={t("approve")}
                    type='default'
                    icon='check'
                    onClick={approveRequest}
                  />
                </div>
                <div className='col-auto'>
                  <Button
                    text={t("reject")}
                    type='normal'
                    icon='close'
                    onClick={rejectRequest}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>) : <></>}

      </Popup>
    </div>

  );
};

export default App;